"use client";

import { useState } from "react";
import { Radio, RadioGroup } from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import useTheme from "../../hooks/useTheme";
import { useThemeContext } from "../ThemeContext";
import { SimpleProduct } from "../../shared/types";

interface ProductCardsProps {
  simpleProducts: SimpleProduct[];
  selectedProductIndex: number | null;
  onProductChange: (index: number) => void;
  currency?: string;
}

const ProductCards: React.FC<ProductCardsProps> = ({
  simpleProducts,
  selectedProductIndex,
  currency,
  onProductChange,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(
    selectedProductIndex !== null ? simpleProducts[selectedProductIndex] : null
  );

  const handleProductChange = (product: SimpleProduct) => {
    const index = simpleProducts.indexOf(product);
    setSelectedProduct(product);
    onProductChange(index);
  };
  const { theme } = useThemeContext();
  const { accent1, accent2 } = useTheme(theme); // Use theme hook

  return (
    <fieldset>
      <RadioGroup
        value={selectedProduct}
        onChange={handleProductChange}
        className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4"
      >
        {simpleProducts.map((product, index) => (
          <Radio
            key={index}
            value={product}
            aria-label={product.name}
            aria-describedby={`${product.headline} ${product.description}`}
            className="group relative flex cursor-pointer rounded-lg border border-gray-300 bg-white p-4 shadow-sm focus:outline-none data-[focus]:border-primary-600 data-[focus]:ring-2 data-[focus]:ring-primary-600"
          >
            <span className="flex flex-1">
              <span className="flex flex-col">
                <span className="block text-lg font-medium text-gray-900">
                  {product.name}
                </span>
                {product.headline && (
                  <span className="mt-1 flex items-center text-sm text-gray-500">
                    {product.headline}
                  </span>
                )}
                {product.description && (
                  <span className="mt-6 text-sm text-gray-500">
                    {product.description}
                  </span>
                )}
                {product.affirm && (
                  <div className="text-sm text-gray-500">Affirm Available</div>
                )}
                <span className="mt-6 text-sm font-medium text-gray-900">
                  {currency}
                  {product.price.toFixed(2)}
                </span>
              </span>
            </span>
            <CheckCircleIcon
              aria-hidden="true"
              className={`h-5 w-5 ${accent1} [.group:not([data-checked])_&]:invisible`}
            />
            <span
              aria-hidden="true"
              className={`pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-[focus]:border ${accent2} `}
            />
          </Radio>
        ))}
      </RadioGroup>
    </fieldset>
  );
};

export default ProductCards;
